import { ApiService } from "../utils/ApiService.js";
import { AppConfig } from "../utils/AppConfig.js";
import { SelloLocal } from "../utils/SelloLocal.js";
import { AuthService } from "./AuthService.js";

/**
 * Revalidacion por sello: en vez de volver a bajar las polizas cada vez, se le pregunta al back
 * si algo cambio bajo el documento del usuario. Es una consulta de ~80 bytes contra un campo
 * indexado; el listado completo solo se baja cuando hace falta. Ver data/README.md (GET estado).
 */
export class EstadoService {
  /**
   * Sello actual del back.
   * El TTL lo maneja ApiCache: si ya se pregunto hace menos de VITE_SELLO_TTL, devuelve el valor
   * guardado sin tocar la red. Por eso no hay logica de throttle escrita a mano.
   */
  static async sello() {
    const resp = await ApiService.get("estado", AuthService.getToken(), {
      cacheTiempo: AppConfig.api.selloTTL,
    });
    if (resp?.status !== 1) return null;
    return resp.message?.Sello ?? null;
  }

  /**
   * Revalida en segundo plano, despues de que la pantalla ya se pinto con lo guardado.
   * `recargar` es la funcion que vuelve a bajar y repintar; se la llama solo si hubo cambios.
   *
   * NUNCA tira: si algo falla, la pantalla se queda con lo que ya estaba mostrando. Ese es el
   * punto de todo esto — sin señal la app sigue sirviendo.
   *
   * @returns {Promise<boolean>} true si hubo datos nuevos y se repinto.
   */
  static async revalidar(recargar) {
    try {
      const selloBack = await this.sello();
      if (!SelloLocal.hayCambio(selloBack)) return false;

      ApiService.invalidate("polizas", AuthService.getToken());
      await recargar();

      // El sello se guarda DESPUES de que los datos bajaron bien. Al reves —guardarlo primero—
      // un corte de red dejaria el sello nuevo junto a datos viejos y la app no volveria a
      // refrescar nunca.
      SelloLocal.guardar(selloBack);
      return true;
    } catch (e) {
      if (AppConfig.features?.debug) console.warn("Revalidacion fallida:", e);
      return false;
    }
  }

  /** Tras una baja exitosa forzada por el usuario (boton ↻), el sello local queda al dia. */
  static async sincronizar() {
    try {
      const selloBack = await this.sello();
      if (selloBack) SelloLocal.guardar(selloBack);
    } catch (e) {
      if (AppConfig.features?.debug) console.warn("No se pudo sincronizar el sello:", e);
    }
  }
}
